<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import ChannelSelector from 'components/ChannelSelector.vue';
import Button from 'next/button/Button.vue';
import Label from 'next/label/Label.vue';
import ViewWrapper from './ViewWrapper.vue';

const { t } = useI18n();
const router = useRouter();

const CHANNEL_INBOX_TYPES = {
  whatsapp: INBOX_TYPES.WHATSAPP,
  facebook: INBOX_TYPES.FB,
  instagram: INBOX_TYPES.INSTAGRAM,
};

const inboxes = useMapGetter('inboxes/getInboxes');

const channels = [
  {
    key: 'whatsapp',
    icon: 'i-woot-whatsapp',
    title: t('WGPT_ONBOARDING.CHANNELS.WHATSAPP.TITLE'),
    description: t('WGPT_ONBOARDING.CHANNELS.WHATSAPP.DESCRIPTION'),
  },
  {
    key: 'facebook',
    icon: 'i-woot-messenger',
    title: t('WGPT_ONBOARDING.CHANNELS.FACEBOOK.TITLE'),
    description: t('WGPT_ONBOARDING.CHANNELS.FACEBOOK.DESCRIPTION'),
  },
  {
    key: 'instagram',
    icon: 'i-woot-instagram',
    title: t('WGPT_ONBOARDING.CHANNELS.INSTAGRAM.TITLE'),
    description: t('WGPT_ONBOARDING.CHANNELS.INSTAGRAM.DESCRIPTION'),
  },
];

const channelsWithState = computed(() =>
  channels.map(c => {
    const connected = inboxes.value.some(
      inbox => inbox.channel_type === CHANNEL_INBOX_TYPES[c.key]
    );
    return {
      ...c,
      connected,
    };
  })
);

const hasConnectedChannel = computed(() =>
  channelsWithState.value.some(c => c.connected)
);

const selectChannel = key => {
  router.push({ name: `wgpt_onboarding_channels_${key}` });
};
</script>

<template>
  <ViewWrapper>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div
        v-for="channel in channelsWithState"
        :key="channel.key"
        class="relative"
      >
        <ChannelSelector
          :title="channel.title"
          :description="channel.description"
          :icon="channel.icon"
          @click="selectChannel(channel.key)"
        />
        <div v-if="channel.connected" class="absolute top-6 end-3">
          <Label
            :label="t('WGPT_ONBOARDING.CHANNELS.CONNECTED')"
            color="teal"
            compact
          />
        </div>
      </div>
    </div>

    <template #actions>
      <Button
        :label="t('WGPT_ONBOARDING.CHANNELS.BACK')"
        variant="link"
        color="slate"
        @click="router.push({ name: 'wgpt_onboarding_integrations' })"
      />
      <Button
        v-if="hasConnectedChannel"
        lg
        :label="t('WGPT_ONBOARDING.CHANNELS.CONTINUE')"
        color="blue"
        @click="router.push({ name: 'wgpt_onboarding_finish' })"
      />
      <Button
        v-else
        :label="t('WGPT_ONBOARDING.CHANNELS.SKIP')"
        variant="link"
        color="slate"
        @click="router.push({ name: 'wgpt_onboarding_finish' })"
      />
    </template>
  </ViewWrapper>
</template>
